/* eslint-disable react-refresh/only-export-components */
import React, { useCallback, useId, useMemo, useState } from 'react';
import {
  Button,
  VirtualizedCombobox,
  type VirtualizedComboboxOption,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { isLockedVisitStatus } from '../task/lockedTask';
import { canEditEpisodeManagerFields } from '../../utils/chartEditPermissions';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import { getAvailableClinicians, updateVisit } from '../../services/api';
import type {
  AvailableClinicianResponse,
  DisciplineType,
  ScheduleReportRow,
  TaskStatus,
} from '../../services/api/types.gen';
import { DISCIPLINETYPE_VALUES } from '../../services/api/models/DisciplineType';
import { getUserFacingApiMessageAsync } from '../../utils/apiErrorDetail';
import {
  clinicianMatchesDiscipline,
  cliniciansToSelectOptions,
  getClinicianDisplayName,
} from '../../utils/clinicianUtils';

export interface ScheduleReportClinicianCellProps {
  row: ScheduleReportRow;
  onClinicianChange?: (
    visitId: string,
    clinicianId: string | null,
    clinicianName: string,
    credentials?: string | null,
    clinicianUserId?: string | null
  ) => void;
}

const CLINICIANS_CACHE_TTL_MS = 5 * 60 * 1000;

interface CachedClinicians {
  clinicians: AvailableClinicianResponse[];
  fetchedAt: number;
}

// In-memory cache for available clinicians across cell instances by clinic_id.
const cliniciansCache = new Map<string, CachedClinicians>();
const cliniciansInFlight = new Map<
  string,
  Promise<AvailableClinicianResponse[]>
>();

export async function fetchAvailableClinicians(
  clinicId: string
): Promise<AvailableClinicianResponse[]> {
  const cached = cliniciansCache.get(clinicId);
  const now = Date.now();
  if (cached && now - cached.fetchedAt < CLINICIANS_CACHE_TTL_MS) {
    return cached.clinicians;
  }
  if (cliniciansInFlight.has(clinicId)) {
    return cliniciansInFlight.get(clinicId)!;
  }

  const promise = (async () => {
    try {
      const response = await getAvailableClinicians({
        query: { clinic_id: clinicId },
        throwOnError: true,
      });
      if (response.data) {
        const clinicians = response.data as AvailableClinicianResponse[];
        cliniciansCache.set(clinicId, {
          clinicians,
          fetchedAt: Date.now(),
        });
        return clinicians;
      }
      return [];
    } finally {
      cliniciansInFlight.delete(clinicId);
    }
  })();

  cliniciansInFlight.set(clinicId, promise);
  return promise;
}

export function clearCliniciansCache(clinicId?: string): void {
  if (clinicId) {
    cliniciansCache.delete(clinicId);
    cliniciansInFlight.delete(clinicId);
  } else {
    cliniciansCache.clear();
    cliniciansInFlight.clear();
  }
}

/** The report row carries the discipline as a string; the API only emits canonical values. */
export function toDisciplineType(
  value: string | null | undefined
): DisciplineType | undefined {
  return DISCIPLINETYPE_VALUES.find(d => d === value);
}

/** What the Schedule Report column shows: the server label omits credentials. */
export function formatColumnName(c: AvailableClinicianResponse): string {
  return getClinicianDisplayName({ ...c, clinician_credentials: null });
}

const compareByName = (
  a: AvailableClinicianResponse,
  b: AvailableClinicianResponse
) => {
  const lastA = (a.clinician_last_name || a.user_last_name).toLowerCase();
  const lastB = (b.clinician_last_name || b.user_last_name).toLowerCase();
  const cmp = lastA.localeCompare(lastB);
  if (cmp !== 0) return cmp;
  const firstA = (a.clinician_first_name || a.user_first_name).toLowerCase();
  const firstB = (b.clinician_first_name || b.user_first_name).toLowerCase();
  return firstA.localeCompare(firstB);
};

export function formatClinicianOptions(
  clinicians: AvailableClinicianResponse[],
  visitDiscipline?: DisciplineType,
  currentAssignedUserClinicId?: string | null
): VirtualizedComboboxOption[] {
  // The current assignee stays listed even when their discipline no longer matches.
  const candidates = clinicians.filter(
    c =>
      clinicianMatchesDiscipline(c, visitDiscipline) ||
      c.id === currentAssignedUserClinicId
  );
  const isMatch = (c: AvailableClinicianResponse) =>
    Boolean(visitDiscipline) && clinicianMatchesDiscipline(c, visitDiscipline);
  // The shared builder applies the assignable filter and drops blank labels.
  const matching = cliniciansToSelectOptions(
    candidates.filter(isMatch).sort(compareByName),
    'clinician'
  );
  const other = cliniciansToSelectOptions(
    candidates.filter(c => !isMatch(c)).sort(compareByName),
    'clinician'
  );
  const flagMatches = other.length > 0;

  return [
    ...matching.map(o => ({
      value: o.value,
      label: flagMatches ? `${o.label} (Discipline Match)` : o.label,
    })),
    ...other.map(o => ({ value: o.value, label: o.label })),
  ];
}

export const ScheduleReportClinicianCell: React.FC<
  ScheduleReportClinicianCellProps
> = ({ row, onClinicianChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingClinicians, setIsLoadingClinicians] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [clinicianOptions, setClinicianOptions] = useState<
    VirtualizedComboboxOption[]
  >([]);
  const [cliniciansRaw, setCliniciansRaw] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [optimisticName, setOptimisticName] = useState<string | null>(null);
  const [optimisticCredentials, setOptimisticCredentials] = useState<
    string | null | undefined
  >(undefined);

  const comboboxId = useId();
  const toast = useToast();

  const { user, hasPermission } = useStore(
    useShallow(s => ({
      user: s.user,
      hasPermission: s.hasPermission,
    }))
  );

  const isLocked = isLockedVisitStatus(row.status as TaskStatus);
  const hasVisitId = Boolean(row.visit_id);

  const canEdit = useMemo(() => {
    if (!hasVisitId || isLocked || !row.clinic_id) return false;
    return canEditEpisodeManagerFields({
      assignedClinicianUserId: row.assigned_clinician_user_id ?? null,
      currentUserId: user?.id,
      clinicId: row.clinic_id,
      clinicPermissions: user?.user_clinics,
      activeClinicIds: user?.active_clinic_ids,
      hasAgencyPermission: hasPermission,
    });
  }, [
    hasVisitId,
    isLocked,
    row.clinic_id,
    row.assigned_clinician_user_id,
    user?.id,
    user?.user_clinics,
    user?.active_clinic_ids,
    hasPermission,
  ]);

  const handleOpenEdit = useCallback(async () => {
    if (!row.clinic_id || isSaving) return;
    setIsEditing(true);
    setIsLoadingClinicians(true);
    try {
      const data = await fetchAvailableClinicians(row.clinic_id);
      setCliniciansRaw(data);
      setClinicianOptions(
        formatClinicianOptions(
          data,
          toDisciplineType(row.discipline),
          row.assigned_clinician_user_clinic_id
        )
      );
    } catch {
      toast.error('Failed to load available clinicians');
      setIsEditing(false);
    } finally {
      setIsLoadingClinicians(false);
    }
  }, [
    row.clinic_id,
    row.discipline,
    row.assigned_clinician_user_clinic_id,
    isSaving,
    toast,
  ]);

  const handleSelectClinician = useCallback(
    async (selectedUserClinicId: string) => {
      if (!row.visit_id || isSaving) return;
      const targetClinician = cliniciansRaw.find(
        c => c.id === selectedUserClinicId
      );
      const newDisplayName = targetClinician
        ? formatColumnName(targetClinician)
        : selectedUserClinicId;
      const newCredentials = targetClinician?.clinician_credentials ?? null;
      const fullDisplayName = targetClinician
        ? getClinicianDisplayName(targetClinician)
        : selectedUserClinicId;

      const previousName = optimisticName ?? row.assigned_clinician;
      const previousCredentials = row.clinician_credentials;

      // Optimistic update
      setOptimisticName(newDisplayName);
      setOptimisticCredentials(newCredentials);
      onClinicianChange?.(
        row.visit_id,
        selectedUserClinicId,
        newDisplayName,
        newCredentials,
        targetClinician?.user_id ?? null
      );
      setIsSaving(true);
      setIsEditing(false);

      try {
        await updateVisit({
          path: { visit_id: row.visit_id },
          body: { assigned_clinician_id: selectedUserClinicId },
          throwOnError: true,
        });
        toast.success(`Visit reassigned to ${fullDisplayName}`);
      } catch (err: unknown) {
        if (row.clinic_id) {
          clearCliniciansCache(row.clinic_id);
        }
        // Rollback
        setOptimisticName(null);
        setOptimisticCredentials(undefined);
        onClinicianChange?.(
          row.visit_id,
          row.assigned_clinician_user_clinic_id ?? null,
          previousName || '',
          previousCredentials,
          row.assigned_clinician_user_id ?? null
        );
        const message = await getUserFacingApiMessageAsync(
          err,
          'Unable to reassign visit. The visit may have been signed or completed by another user.'
        );
        toast.error(message);
      } finally {
        setIsSaving(false);
      }
    },
    [
      row.visit_id,
      row.clinic_id,
      row.assigned_clinician,
      row.assigned_clinician_user_clinic_id,
      row.assigned_clinician_user_id,
      row.clinician_credentials,
      cliniciansRaw,
      optimisticName,
      isSaving,
      onClinicianChange,
      toast,
    ]
  );

  const displayedClinician = (optimisticName ?? row.assigned_clinician)?.trim();
  const rawCredentials =
    optimisticCredentials !== undefined
      ? optimisticCredentials
      : row.clinician_credentials;
  const credentials = rawCredentials ? rawCredentials.trim() : null;

  if (!canEdit) {
    return (
      <div
        className='flex items-center gap-1.5 text-sm'
        data-testid='schedule-report-clinician-cell'
      >
        <span className='font-medium text-foreground'>
          {displayedClinician || 'Unassigned'}
        </span>
        {credentials && (
          <span className='text-muted-foreground text-xs font-normal'>
            ({credentials})
          </span>
        )}
      </div>
    );
  }

  if (isEditing) {
    if (isLoadingClinicians) {
      return (
        <div
          className='flex items-center gap-2 text-sm text-muted-foreground'
          data-testid='schedule-report-clinician-cell'
        >
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
          <span>Loading clinicians...</span>
        </div>
      );
    }

    return (
      <div
        className='flex items-center gap-1.5 min-w-[200px]'
        data-testid='schedule-report-clinician-cell'
      >
        <div className='flex-1'>
          <VirtualizedCombobox
            id={comboboxId}
            options={clinicianOptions}
            value={row.assigned_clinician_user_clinic_id || ''}
            onValueChange={val => {
              if (val) {
                void handleSelectClinician(val);
              }
            }}
            placeholder='Select clinician...'
            searchPlaceholder='Search clinicians...'
          />
        </div>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-8 px-2 text-xs'
          onClick={() => setIsEditing(false)}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div
      className='flex items-center gap-2 group text-sm'
      data-testid='schedule-report-clinician-cell'
    >
      <div className='flex items-center gap-1.5 min-w-0 truncate'>
        <span className='font-medium text-foreground truncate'>
          {displayedClinician || 'Unassigned'}
        </span>
        {credentials && (
          <span className='text-muted-foreground text-xs font-normal shrink-0'>
            ({credentials})
          </span>
        )}
        {isSaving && (
          <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0' />
        )}
      </div>
      {!isSaving && (
        <button
          type='button'
          className='text-xs text-primary hover:underline cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring rounded px-1 shrink-0'
          onClick={() => void handleOpenEdit()}
          aria-label={`Reassign visit for ${displayedClinician || 'Unassigned'}`}
        >
          Reassign
        </button>
      )}
    </div>
  );
};
